// SourceSelectProvider.jsx - MODIFIÉ POUR PERPLEXITY
// Composant pour sélectionner les fournisseurs de sources avec support Perplexity
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Tooltip, CircularProgress, Alert} from '@mui/material';
import { Info, CheckCircle, Search, Public, School, Newspaper, DataUsage, HelpOutline } from '@mui/icons-material';
import sourceService from './SourceService';

/**
 * Configuration des icônes pour les providers
 * 🆕 AJOUT: Perplexity avec son icône
 */
const providerIcons = {
  // Providers généraux
  'Perplexity': '🔍',  // 🆕 NOUVEAU
  'Google': '🌐',
  'OpenAI': '🤖',
  'DuckDuckGo': '🦆',
  'Aladinn': '✨',
  'VideoEducation': '🎬',
  
  // Providers académiques
  'Wikipedia': '📖',
  'Google Scholar': '🎓',
  'arXiv': '📄',
  'PubMed': '🏥',
  
  // Providers médias
  'YouTube': '📺',
  'Twitter/X': '🐦',
  'News Media': '📰',
  
  // Providers de données
  'Open Data': '📊',
  'International Orgs': '🌍'
};

/**
 * Configuration des badges pour les providers
 * 🆕 AJOUT: Badge "Nouveau" pour Perplexity
 */
const providerBadges = {
  'Perplexity': { text: 'Nouveau', color: 'green' }  // 🆕 NOUVEAU
};

/**
 * Composant de sélection des fournisseurs de sources
 */
const SourceSelectProvider = forwardRef(({ 
  selectedProviders = [], 
  onChange, 
  userAuthToken,
  initialMode = 'auto',
  onModeChange
}, ref) => {
  // États
  const [providerData, setProviderData] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(selectedProviders);
  const [recommendedProviders, setRecommendedProviders] = useState([]);
  const [isInternalUpdate, setIsInternalUpdate] = useState(false);
  
  // Icônes pour chaque catégorie
  const categoryIcons = {
    general: <Search className="h-5 w-5 text-blue-600" />,
    academic: <School className="h-5 w-5 text-green-600" />,
    media: <Newspaper className="h-5 w-5 text-yellow-600" />,
    data: <DataUsage className="h-5 w-5 text-purple-600" />,
    ai: <HelpOutline className="h-5 w-5 text-indigo-600" />  // 🆕 Catégorie pour Aladinn
  };
  
  /**
   * 🆕 NOUVEAU: Fonction utilitaire pour obtenir le badge d'un provider
   */
  const getProviderBadge = (providerName) => {
    return providerBadges[providerName] || null;
  };
  
  /**
   * 🆕 NOUVEAU: Fonction pour afficher un provider avec son icône et badge
   */
  const renderProviderName = (providerName, size = 'md') => {
    const icon = providerIcons[providerName];
    const badge = getProviderBadge(providerName);
    
    const iconSizes = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg'
    };
    
    const badgeColors = {
      green: 'bg-green-100 text-green-700 border-green-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-300'
    };
    
    return (
      <span className="inline-flex items-center">
        {icon && (
          <span 
            className={`mr-1.5 ${iconSizes[size]} flex-shrink-0`}
            role="img" 
            aria-label={providerName}
          >
            {icon}
          </span>
        )}
        <span>{providerName}</span>
        {badge && (
          <span 
            className={`ml-1.5 text-xs font-semibold px-1.5 py-0.5 rounded border ${
              badgeColors[badge.color] || badgeColors.green
            }`}
          >
            {badge.text}
          </span>
        )}
      </span>
    );
  };
  
  // Exposer des méthodes au parent via la référence
  useImperativeHandle(ref, () => ({
    updateRecommendations: (recommendations) => {
      console.log("Mise à jour des providers recommandés:", recommendations);
      
      if (Array.isArray(recommendations)) {
        setRecommendedProviders(recommendations);
        
        if (mode === 'guided' || mode === 'auto') {
          setIsInternalUpdate(true);
          setSelected(recommendations);
          
          if (mode === 'guided') {
            console.log("Mode guidé: sélection mise à jour avec", recommendations.length, "providers");
          }
        }
      }
    }
  }));
  
  // Chargement des métadonnées des fournisseurs au montage
  useEffect(() => {
    loadProviderMetadata();
  }, [userAuthToken]);
  
  // Mettre à jour la sélection parent
  useEffect(() => {
    if (onChange && selected) {
      console.log("SourceSelectProvider: Mise à jour des providers sélectionnés:", selected);
      
      if (isInternalUpdate) {
        onChange({ selectedProviders: selected, _internal: true });
        setIsInternalUpdate(false);
      } else {
        onChange({ selectedProviders: selected });
      }
    }
  }, [selected, onChange, isInternalUpdate]);
  
  /**
   * Charge les métadonnées des fournisseurs disponibles
   */
  const loadProviderMetadata = async () => {
    if (!userAuthToken) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const metadata = await sourceService.getProvidersMetadata(userAuthToken);
      setProviderData(metadata);
      
      if (metadata && metadata.categories) {
        const firstCategory = Object.keys(metadata.categories)[0];
        setExpandedCategory(firstCategory);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des métadonnées:', err);
      setError('Impossible de charger les données des fournisseurs.');
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Gère l'expansion/contraction d'une catégorie
   */
  const handleCategoryToggle = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };
  
  /**
   * Gère la sélection/désélection d'un fournisseur
   */
  const handleProviderToggle = (providerName) => {
    console.log("Provider toggled:", providerName);
    
    setSelected(prev => {
      if (prev.includes(providerName)) {
        return prev.filter(name => name !== providerName);
      }
      return [...prev, providerName];
    });
  };
  
  /**
   * Change le mode de sélection
   */
  const handleModeChange = (newMode) => {
    console.log("Changement de mode:", newMode);
    setMode(newMode);
    setIsInternalUpdate(true);
    
    if (newMode === 'auto') {
      setSelected([]);
    }
    
    if (newMode === 'guided' && recommendedProviders.length > 0) {
      setSelected(recommendedProviders);
    }
    
    if (onModeChange) {
      onModeChange(newMode);  
    }
  };
  
  /**
   * Sélectionne tous les fournisseurs recommandés
   */
  const selectRecommended = () => {
    if (recommendedProviders.length > 0) {
      setSelected([...recommendedProviders]);
    }
  };
  
  /**
   * Sélectionne tous les fournisseurs d'une catégorie
   */
  const selectAllInCategory = (category) => {
    if (!providerData || !providerData.categories[category]) return;
    
    const categoryProviders = providerData.categories[category].providers || [];
    
    setSelected(prev => {
      const fromOtherCategories = prev.filter(name => 
        !categoryProviders.includes(name)
      );
      return [...fromOtherCategories, ...categoryProviders];
    });
  };
  
  // Gestion du chargement
  if (loading && !providerData) {
    return (
      <div className="flex justify-center items-center p-6">
        <CircularProgress />
        <span className="ml-2 text-gray-600">Chargement des fournisseurs...</span>
      </div>
    );
  }
  
  // Gestion des erreurs
  if (error) {
    return (
      <Alert severity="error" className="mb-4">
        {error}
      </Alert>
    );
  }
  
  // Si pas de données
  if (!providerData) {
    return (
      <Alert severity="info" className="mb-4">
        Aucun fournisseur disponible
      </Alert>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Sélection du mode */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-md border border-gray-200 p-4">
        <div className="flex items-center space-x-2">
          <span className="font-medium text-gray-700">Mode de sélection:</span>
          
          {['auto', 'guided', 'expert'].map(modeOption => (
            <button
              key={modeOption}
              type="button"
              onClick={() => handleModeChange(modeOption)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                mode === modeOption
                  ? 'bg-purple-100 border-purple-300 text-purple-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {modeOption === 'auto' && 'Automatique'}
              {modeOption === 'guided' && 'Guidé'}
              {modeOption === 'expert' && 'Expert'}
            </button>
          ))}
        </div>
        
        <Tooltip title="En mode automatique, les sources les plus pertinentes sont sélectionnées automatiquement. En mode guidé, vous pouvez voir et modifier les recommandations. En mode expert, vous choisissez librement tous les fournisseurs.">
          <div className="text-gray-400 cursor-help">
            <HelpOutline className="h-5 w-5" />
          </div>
        </Tooltip>
      </div>
      
      {/* Mode automatique */}
      {mode === 'auto' && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
          En mode automatique, les sources les plus pertinentes sont sélectionnées automatiquement en fonction de votre requête et objectif.
        </div>
      )}
      
      {/* Mode guidé - 🆕 MODIFIÉ: Affichage avec icônes et badges */}
      {mode === 'guided' && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <h4 className="font-medium text-gray-800 mb-3">Sources recommandées:</h4>
          
          {recommendedProviders.length > 0 ? (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {recommendedProviders.map(provider => (
                  <button
                    key={provider}
                    type="button"
                    onClick={() => handleProviderToggle(provider)}
                    className={`flex items-center px-3 py-1.5 rounded-lg border transition-colors ${
                      selected.includes(provider)
                        ? 'bg-green-100 border-green-300 text-green-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {/* 🆕 UTILISATION: Render avec icône et badge */}
                    {renderProviderName(provider, 'sm')}
                    
                    {selected.includes(provider) && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1.5 text-green-700" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
              <div>
                <button
                  type="button"
                  onClick={selectRecommended}
                  className="inline-flex items-center px-3 py-1.5 rounded-lg border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                >
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                  Tout sélectionner
                </button>
              </div>
            </div>
          ) : (
            <div className="text-gray-500">
              {loading ? 
                "Chargement des recommandations en cours..." : 
                "Aucune recommandation disponible. Effectuez une recherche pour obtenir des recommandations."}
            </div>
          )}
        </div>
      )}
      
      {/* Liste des fournisseurs par catégorie - 🆕 MODIFIÉ */}
      {(mode === 'expert' || mode === 'guided') && (
        <div className="space-y-3">
          {Object.keys(providerData.categories).map(categoryKey => (
            <div key={categoryKey} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              {/* En-tête de la catégorie */}
              <button
                type="button"
                onClick={() => handleCategoryToggle(categoryKey)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <span className="mr-2">
                    {categoryIcons[categoryKey] || <Public className="h-5 w-5 text-gray-600" />}
                  </span>
                  <span className="font-medium text-gray-800">
                    {providerData.categories[categoryKey].name || categoryKey}
                  </span>
                  <span className="ml-2 bg-purple-100 text-purple-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                    {providerData.categories[categoryKey].providers.length}
                  </span>
                </div>
                <span>
                  {expandedCategory === categoryKey ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                </span>
              </button>
              
              {/* Contenu de la catégorie - 🆕 MODIFIÉ: Affichage avec icônes et badges */}
              {expandedCategory === categoryKey && (
                <div className="p-4">
                  <ul className="divide-y divide-gray-200">
                    {providerData.categories[categoryKey].providers.map(providerName => {
                      const provider = providerData.providers[providerName] || {};
                      const isRecommended = recommendedProviders.includes(providerName);
                      
                      return (
                        <li key={providerName} className="py-2">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id={`provider-${providerName}`}
                              checked={selected.includes(providerName)}
                              onChange={() => handleProviderToggle(providerName)}
                              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                            />
                            <label htmlFor={`provider-${providerName}`} className="ml-3 flex items-center cursor-pointer flex-1">
                              {/* 🆕 UTILISATION: Render avec icône et badge */}
                              {renderProviderName(providerName, 'md')}
                              
                              {isRecommended && (
                                <Tooltip title="Recommandé pour cette recherche" placement="top">
                                  <CheckCircle className="ml-1.5 h-4 w-4 text-green-500" />
                                </Tooltip>
                              )}
                            </label>
                          </div>
                          {provider.description && (
                            <p className="mt-1 text-sm text-gray-500 ml-7">
                              {provider.description}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => selectAllInCategory(categoryKey)}
                      className="inline-flex items-center px-3 py-1.5 text-sm rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors"
                    >
                      Sélectionner tous
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Résumé des sélections - 🆕 MODIFIÉ: Affichage avec icônes et badges */}
      {(mode === 'expert' || mode === 'guided') && selected.length > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <h4 className="font-medium text-gray-800 mb-3">
            Fournisseurs sélectionnés ({selected.length}):
          </h4>
          <div className="flex flex-wrap gap-2">
            {selected.map(provider => (
              <span
                key={provider}
                className="inline-flex items-center px-3 py-1.5 rounded-lg border border-purple-300 text-purple-700 bg-purple-50"
              >
                {/* 🆕 UTILISATION: Render avec icône et badge */}
                {renderProviderName(provider, 'sm')}
                
                <button
                  type="button"
                  onClick={() => handleProviderToggle(provider)}
                  className="ml-1.5 text-purple-500 hover:text-purple-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export default SourceSelectProvider;